import { Alert, Button, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { executeShellCommandJson, type ShellResponse } from '../../api/shell';
import { ResultTable } from '../../components/ResultTable';
import { sanitizeShellArg } from '../../utils/shell';

const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
const TEMPLATES = ['poisoned_drink'] as const;

export function CaseInspector() {
  const [caseId, setCaseId] = useState('');
  const [caseResult, setCaseResult] = useState<ShellResponse | null>(null);
  const [bootstrapResult, setBootstrapResult] = useState<ShellResponse | null>(null);
  const [createResult, setCreateResult] = useState<ShellResponse | null>(null);
  const [difficulty, setDifficulty] = useState<string>('easy');
  const [template, setTemplate] = useState<string>('poisoned_drink');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleGetCase() {
    if (!caseId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await executeShellCommandJson(`case.get ${sanitizeShellArg(caseId)}`);
      setCaseResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleGetBootstrap() {
    if (!caseId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await executeShellCommandJson(`case.bootstrap ${sanitizeShellArg(caseId)}`);
      setBootstrapResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateCase() {
    if (!confirm(`Create a new ${difficulty} case with template "${template}"?`)) return;
    setLoading(true);
    setError('');
    setCreateResult(null);
    try {
      const safeDifficulty = sanitizeShellArg(difficulty);
      const safeTemplate = sanitizeShellArg(template);
      const res = await executeShellCommandJson(`case.create --difficulty ${safeDifficulty} --template ${safeTemplate} --write`);
      setCreateResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleWriteAction(action: 'close' | 'abandon') {
    if (!caseId.trim()) return;
    if (!confirm(`Are you sure you want to ${action} case ${caseId}?`)) return;
    setLoading(true);
    setError('');
    try {
      const res = await executeShellCommandJson(`case.${action} ${sanitizeShellArg(caseId)} --write`);
      setCaseResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Inspect Case</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Case ID"
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                fullWidth
                size="small"
                placeholder="UUID"
              />
              <Button variant="contained" onClick={handleGetCase} disabled={loading}>
                Get
              </Button>
              <Button variant="outlined" onClick={handleGetBootstrap} disabled={loading}>
                Bootstrap
              </Button>
            </Stack>
            {caseResult && <ResultTable data={caseResult} />}
            {bootstrapResult && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Bootstrap State</Typography>
                <ResultTable data={bootstrapResult} />
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Create Case</Typography>
            <Typography variant="body2" color="text.secondary">
              Requires <Chip label="shell.write" size="small" /> permission. Audited.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Difficulty</InputLabel>
                <Select value={difficulty} label="Difficulty" onChange={(e) => setDifficulty(e.target.value)}>
                  {DIFFICULTIES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Template</InputLabel>
                <Select value={template} label="Template" onChange={(e) => setTemplate(e.target.value)}>
                  {TEMPLATES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleCreateCase} disabled={loading}>
                Create
              </Button>
            </Stack>
            {createResult && <ResultTable data={createResult} />}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Case Write Actions</Typography>
            <Typography variant="body2" color="text.secondary">
              Requires <Chip label="shell.write" size="small" /> permission and --write flag. Audited.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => handleWriteAction('close')}
                disabled={loading || !caseId.trim()}
              >
                Close Case
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleWriteAction('abandon')}
                disabled={loading || !caseId.trim()}
              >
                Abandon Case
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
